import {logUtishaDebug, logUtishaInfo, logUtishaWarn} from 'src/app/util/log';

/**
 * Constraint fallback ladders for getUserMedia.
 *
 * The browser's getUserMedia is brittle in the wild:
 *  - a saved deviceId may no longer exist (user unplugged the device)
 *  - a resolution constraint may be OverconstrainedError on the current
 *    default device (common on laptops with integrated webcams)
 *  - NotReadableError appears transiently when another app (or the same app
 *    in a different tab) has momentarily held the device — retrying once
 *    after a brief wait usually succeeds
 *  - a permission revocation surfaces as NotAllowedError which is terminal
 *    and should NOT be retried
 *
 * These helpers encode a fallback ladder so each call site doesn't re-derive
 * the same logic (and drop half of it). The ladder is intentionally short —
 * every rung is one real failure mode we've observed, not speculative safety.
 */

export interface AcquireCameraOptions {
  /** Preferred deviceId if the user has one selected. */
  deviceId?: string;
  /** Preferred width in pixels (HD target). */
  preferredWidth?: number;
  /** Preferred height in pixels (HD target). */
  preferredHeight?: number;
}

export interface AcquireMicrophoneOptions {
  deviceId?: string;
}

/**
 * Acquire a camera MediaStream, degrading gracefully through the fallback
 * ladder. Throws only on terminal errors (permission denied, no camera
 * present on the device at all). Never throws NotReadableError or
 * OverconstrainedError — those are caught and absorbed by stepping down.
 */
export async function acquireCameraWithFallback(opts: AcquireCameraOptions = {}): Promise<MediaStream> {
  const width = opts.preferredWidth ?? 1280;
  const height = opts.preferredHeight ?? 720;

  const ladder: Array<{ label: string; constraints: MediaStreamConstraints }> = [];
  if (opts.deviceId) {
    ladder.push({
      label: `saved-device-hd(${opts.deviceId.slice(0, 8)})`,
      constraints: {
        audio: false,
        video: {
          deviceId: {exact: opts.deviceId},
          width: {ideal: width},
          height: {ideal: height},
        },
      },
    });
    ladder.push({
      label: `saved-device-sd(${opts.deviceId.slice(0, 8)})`,
      constraints: {
        audio: false,
        video: {
          deviceId: {exact: opts.deviceId},
          width: {ideal: 640},
          height: {ideal: 360},
        },
      },
    });
  }
  ladder.push({
    label: 'any-camera-hd',
    constraints: {audio: false, video: {width: {ideal: width}, height: {ideal: height}}},
  });
  ladder.push({
    label: 'any-camera-sd',
    constraints: {audio: false, video: {width: {ideal: 640}, height: {ideal: 360}}},
  });
  ladder.push({label: 'any-camera-default', constraints: {audio: false, video: true}});

  return runLadder('camera', ladder);
}

/**
 * Acquire a microphone MediaStream with the same graceful-degradation
 * treatment. Same error contract as acquireCameraWithFallback.
 */
export async function acquireMicrophoneWithFallback(opts: AcquireMicrophoneOptions = {}): Promise<MediaStream> {
  const ladder: Array<{ label: string; constraints: MediaStreamConstraints }> = [];
  if (opts.deviceId) {
    ladder.push({
      label: `saved-mic(${opts.deviceId.slice(0, 8)})`,
      constraints: {video: false, audio: {deviceId: {exact: opts.deviceId}}},
    });
  }
  ladder.push({label: 'any-mic', constraints: {video: false, audio: true}});
  return runLadder('microphone', ladder);
}

async function runLadder(
  kind: 'camera' | 'microphone',
  ladder: Array<{ label: string; constraints: MediaStreamConstraints }>,
): Promise<MediaStream> {
  let lastError: any;
  for (let i = 0; i < ladder.length; i++) {
    const rung = ladder[i];
    try {
      logUtishaDebug(`acquireMedia(${kind}): trying rung ${i + 1}/${ladder.length} ${rung.label}`);
      const stream = await navigator.mediaDevices.getUserMedia(rung.constraints);
      if (i > 0) {
        logUtishaInfo(`acquireMedia(${kind}): succeeded on fallback rung ${rung.label}`);
      }
      return stream;
    } catch (e: any) {
      lastError = e;
      const name = e?.name || 'UnknownError';
      // NotAllowedError / SecurityError are terminal — no point laddering down.
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        logUtishaWarn(`acquireMedia(${kind}): permission denied (${name}) — aborting ladder`);
        throw e;
      }
      // NotFoundError on the first rung with a specific deviceId means the
      // saved device is gone. Not terminal — ladder will try 'any-camera'.
      // NotReadableError / OverconstrainedError / AbortError all ladder down.
      logUtishaWarn(`acquireMedia(${kind}): rung ${rung.label} failed with ${name}: ${e?.message || e}`);
      // For NotReadableError specifically, wait a beat — another app may
      // have had the device momentarily. A second immediate attempt often
      // succeeds, so retry the SAME rung once before stepping down.
      if (name === 'NotReadableError') {
        await sleep(400);
        try {
          logUtishaDebug(`acquireMedia(${kind}): retrying rung ${rung.label} after NotReadableError`);
          const retry = await navigator.mediaDevices.getUserMedia(rung.constraints);
          logUtishaInfo(`acquireMedia(${kind}): rung ${rung.label} succeeded after NotReadableError retry`);
          return retry;
        } catch (e2: any) {
          lastError = e2;
          logUtishaWarn(`acquireMedia(${kind}): retry of ${rung.label} also failed: ${e2?.message || e2}`);
        }
      }
    }
  }
  throw lastError ?? new Error(`acquireMedia(${kind}): all ladder rungs exhausted`);
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
